import { useEffect, useState } from "react";

import { Redirect } from "react-router-dom";
import { getToken } from "@app/auth/token-store";
import {
  consumeCallback,
  exchangeCode,
  retryAuthorizeOnce,
  startAuthorize,
} from "@app/auth/pkce";
import Loader from "@app/components/Loader/loader";

/**
 * IIDM-3067 T3 — login via authorization code + PKCE S256 + `state` (src/auth/pkce.ts), with the
 * IIDM-3063 public client: no client secret, no refresh token.
 *
 *   no token, no `code`      → start authorize (deep link kept in `localStorage.payload`)
 *   `code` + matching state  → exchange with `code_verifier`, store the token, reload (plan Q2)
 *   `code` + bad/no state    → NO token request; retry authorize once, then show an error
 *   exchange failed          → same retry-once rule
 *   `error` callback         → error shown, no automatic retry (it would loop)
 */
export default function Authorization() {
  const access_token = getToken();

  const [isToken, setIsToken] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (access_token) {
      return;
    }
    (async () => {
      const urlparams = new URLSearchParams(window.location.search);
      if (urlparams.has("error") && !urlparams.has("code")) {
        // The SSO answered with an OAuth error: re-authorizing straight away would loop.
        consumeCallback(window.location.search);
        setFailed(true);
        return;
      }
      if (!urlparams.has("code")) {
        await startAuthorize(window.location.pathname);
        return;
      }
      const callback = consumeCallback(window.location.search);
      const ok = callback ? await exchangeCode(callback.code, callback.verifier) : false;
      if (ok) {
        setIsToken(true);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }
      if (!callback) {
        console.error("IIDM-3067: OAuth callback rejected — state missing or mismatched.");
      }
      if (!retryAuthorizeOnce()) {
        setFailed(true);
      }
    })();
  }, []);

  if (failed) {
    return (
      <div data-testid="auth-error" style={{ padding: 24 }}>
        Sign-in could not be completed. <a href="/">Try again</a>
      </div>
    );
  }

  return (
    <>
      {isToken && <Redirect exact from="/" to="/loading..." />}
      {!isToken && <Loader />}
    </>
  );
}
